import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Subject, catchError, distinctUntilChanged, map, merge, of, shareReplay, startWith, switchMap, tap, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationFilter, NotificationsPage } from '@app/shared/interfaces/notification.interface';
import { AuthService } from '../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.url}notificaciones`;
    private readonly changed = new Subject<void>();
    readonly changes$ = this.changed.asObservable();
    readonly userId$ = inject(AuthService).currentUser$.pipe(
        map(user => user?.id ?? null),
        distinctUntilChanged()
    );
    readonly summary$ = this.userId$.pipe(
        switchMap(id => id
            ? merge(timer(0, 60000), this.changes$).pipe(
                switchMap(() => this.list(1, 1).pipe(
                    map(page => ({ unread: page.unread, error: false })),
                    catchError(() => of({ unread: null, error: true }))
                )),
                startWith({ unread: null, error: false })
            )
            : of({ unread: 0, error: false })),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    list(page: number, limit: number, estado: NotificationFilter = 'all') {
        return this.http.get<NotificationsPage>(this.baseUrl, { params: { page, limit, estado } });
    }

    setRead(id: number, visto: boolean) {
        return this.http.patch<{ id: number; visto: boolean }>(`${this.baseUrl}/${id}/lectura`, { visto })
            .pipe(tap(() => this.changed.next()));
    }

    markAllRead() {
        return this.http.patch<{ updated: number }>(`${this.baseUrl}/leidas`, {})
            .pipe(tap(() => this.changed.next()));
    }

    refresh() {
        this.changed.next();
    }
}
